/**
 * Factory for creating MUI themes from brand tokens.
 *
 * This file implements the "MUI Theme Layer" of the three-layer architecture:
 * 1. MUI Theme (this file) - handles colors, hover states, selected states
 * 2. Component Tokens - status colors (recording red, ready green), sizing
 * 3. Component Styles - minimal sx props for layout, animations only
 *
 * By centralizing component styling in the MUI theme, we eliminate !important
 * overrides and scattered factory functions throughout the codebase.
 */

import { alpha, createTheme, darken, Theme } from '@mui/material/styles';
import type { DerivedBrandTokens } from '@ptek/widget-core';
import { STATUS_COLORS, RECORDING_COLORS } from '../constants/statusColors';

/**
 * Contrast text color for use on colored backgrounds (buttons, icons, etc.).
 * Centralized here to avoid hardcoded #ffffff throughout the theme.
 */
const CONTRAST_TEXT = '#ffffff';

declare module '@mui/material/styles' {
  interface Palette {
    custom: {
      backgroundAlt: string;
      backgroundHover: string;
      backgroundSelected: string;
      utilityBarBackground: string;
      utilityBarText: string;
      secondaryButton: string;
      activeTab: string;
      audioWave: string;
      menuActive: string;
      menuHover: string;
      neutralBg: string;
      contrastText: string;
      scrollbarTrack: string;
      scrollbarThumb: string;
      scrollbarThumbHover: string;
      menuShadow: string;
      dropdownShadow: string;
      cardShadow: string;
      subtleShadow: string;
      dialogHeaderBg: string;
      warningBgLight: string;
      warningBorderLight: string;
      status: {
        ready: { bg: string; text: string; solid: string };
        recording: { bg: string; text: string; solid: string };
        transcribing: { bg: string; text: string; solid: string };
        paused: { bg: string; text: string; solid: string };
        starting: { bg: string; text: string; solid: string };
        completed: { bg: string; text: string; solid: string };
        warning: { bg: string; text: string; solid: string };
      };
      recordingIndicator: string;
    };
  }
  interface PaletteOptions {
    custom?: {
      backgroundAlt?: string;
      backgroundHover?: string;
      backgroundSelected?: string;
      utilityBarBackground?: string;
      utilityBarText?: string;
      secondaryButton?: string;
      activeTab?: string;
      audioWave?: string;
      menuActive?: string;
      menuHover?: string;
      neutralBg?: string;
      contrastText?: string;
      scrollbarTrack?: string;
      scrollbarThumb?: string;
      scrollbarThumbHover?: string;
      menuShadow?: string;
      dropdownShadow?: string;
      cardShadow?: string;
      subtleShadow?: string;
      dialogHeaderBg?: string;
      warningBgLight?: string;
      warningBorderLight?: string;
      status?: {
        ready?: { bg: string; text: string; solid: string };
        recording?: { bg: string; text: string; solid: string };
        transcribing?: { bg: string; text: string; solid: string };
        paused?: { bg: string; text: string; solid: string };
        starting?: { bg: string; text: string; solid: string };
        completed?: { bg: string; text: string; solid: string };
        warning?: { bg: string; text: string; solid: string };
      };
      recordingIndicator?: string;
    };
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonOwnProps {
    variant?: 'recording';
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    transcribe: true;
    'transcribe-outlined': true;
    pill: true;
    'pill-outlined': true;
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    panel: true;
    dropdown: true;
    card: true;
    'card-warning': true;
    'card-error': true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    formLabel: true;
    sectionTitle: true;
    menuItemPrimary: true;
    menuItemSecondary: true;
    placeholder: true;
    dialogTitle: true;
    dialogBody: true;
    helperText: true;
    errorText: true;
    successText: true;
    statusText: true;
    statusReady: true;
    statusRecording: true;
    statusTranscribing: true;
    statusPaused: true;
    statusStarting: true;
    statusCompleted: true;
    statusWarning: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    transcript: true;
  }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsColorOverrides {
    warning: true;
  }
}

/**
 * Creates an MUI theme from derived brand tokens.
 *
 * This factory takes the resolved brand tokens and generates a complete
 * MUI theme that can be used with ThemeProvider. Component overrides are
 * defined here so that components can use MUI props (color="primary",
 * variant="contained") instead of inline sx color definitions.
 *
 * @param brand Derived brand tokens from widget-core
 * @returns MUI Theme configured with brand colors and component overrides
 *
 * @example
 * ```tsx
 * const brand = useBrandTheme();
 * const muiTheme = createBrandedMuiTheme(brand);
 *
 * return (
 *   <ThemeProvider theme={muiTheme}>
 *     <App />
 *   </ThemeProvider>
 * );
 * ```
 */
export function createBrandedMuiTheme(brand: DerivedBrandTokens): Theme {
  return createTheme({
    palette: {
      primary: {
        main: brand.primaryColor,
        dark: brand.primaryColorDark,
        light: brand.primaryColorLight,
      },
      secondary: {
        main: brand.textColorSecondary,
      },
      success: {
        main: brand.successColor,
      },
      error: {
        main: brand.errorColor,
      },
      warning: {
        main: brand.warningColor,
      },
      info: {
        main: brand.primaryColor,
      },
      text: {
        primary: brand.textColor,
        secondary: brand.textColorSecondary,
        disabled: brand.textColorMuted,
      },
      background: {
        default: brand.backgroundColor,
        paper: brand.backgroundColor,
      },
      divider: brand.dividerColor,
      custom: {
        backgroundAlt: brand.backgroundColorAlt,
        backgroundHover: brand.backgroundHover,
        backgroundSelected: brand.backgroundColorSelected,
        utilityBarBackground: brand.utilityBarBackgroundColor,
        utilityBarText: brand.utilityBarTextColor,
        secondaryButton: brand.secondaryButtonColor,
        activeTab: brand.activeTabColor,
        audioWave: brand.audioWaveColor,
        menuActive: brand.menuActiveColor,
        menuHover: brand.menuHoverColor,
        neutralBg: alpha(brand.textColor, 0.12),
        contrastText: CONTRAST_TEXT,
        scrollbarTrack: alpha(brand.textColor, 0.05),
        scrollbarThumb: alpha(brand.textColor, 0.2),
        scrollbarThumbHover: alpha(brand.textColor, 0.3),
        menuShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        dropdownShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        cardShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        subtleShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        dialogHeaderBg: alpha(brand.textColor, 0.04),
        warningBgLight: alpha(brand.warningColor, 0.04),
        warningBorderLight: alpha(brand.warningColor, 0.2),
        status: STATUS_COLORS,
        recordingIndicator: RECORDING_COLORS.recording,
      },
    },
    typography: {
      fontFamily: brand.fontFamily,
      fontSize: parseInt(brand.fontSizeBase, 10) || 14,
    },
    shape: {
      borderRadius: parseInt(brand.borderRadius, 10) || 4,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: brand.borderRadius,
          },
          containedPrimary: {
            backgroundColor: brand.primaryColor,
            color: CONTRAST_TEXT,
            '&:hover': {
              backgroundColor: brand.primaryColorDark,
            },
            '&:disabled': {
              backgroundColor: brand.textColorMuted,
              color: brand.backgroundColor,
            },
          },
          outlinedPrimary: {
            borderColor: brand.secondaryButtonColor,
            color: brand.secondaryButtonColor,
            '&:hover': {
              backgroundColor: alpha(brand.secondaryButtonColor, 0.08),
              borderColor: brand.secondaryButtonColor,
            },
            '&:disabled': {
              borderColor: brand.textColorMuted,
              color: brand.textColorMuted,
            },
          },
          textPrimary: {
            color: brand.primaryColor,
            '&:hover': {
              backgroundColor: brand.primaryColorHover,
            },
          },
        },
        variants: [
          {
            props: { variant: 'transcribe' as const },
            style: {
              borderRadius: 24,
              padding: '6px 16px',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 160,
              height: 40,
              backgroundColor: brand.primaryColor,
              color: CONTRAST_TEXT,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                backgroundColor: brand.primaryColorDark,
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 16px ${alpha(brand.primaryColor, 0.3)}`,
              },
              '&:disabled': {
                backgroundColor: alpha(brand.textColor, 0.12),
                color: alpha(brand.textColor, 0.26),
              },
            },
          },
          {
            props: { variant: 'transcribe-outlined' as const },
            style: {
              borderRadius: 24,
              padding: '6px 16px',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 160,
              height: 40,
              backgroundColor: 'transparent',
              border: `2px solid ${brand.secondaryButtonColor}`,
              color: brand.secondaryButtonColor,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                backgroundColor: brand.secondaryButtonColor,
                color: CONTRAST_TEXT,
              },
              '&:disabled': {
                backgroundColor: 'transparent',
                borderColor: brand.textColorMuted,
                color: brand.textColorMuted,
              },
            },
          },
          {
            props: { variant: 'transcribe' as const, color: 'error' as const },
            style: {
              backgroundColor: brand.errorColor,
              '&:hover': {
                backgroundColor: darken(brand.errorColor, 0.2),
              },
              '&:disabled': {
                backgroundColor: alpha(brand.errorColor, 0.6),
                color: alpha(CONTRAST_TEXT, 0.7),
              },
            },
          },
          {
            props: { variant: 'pill' as const },
            style: {
              width: '100%',
              borderRadius: 24,
              padding: '8px 16px',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: brand.primaryColor,
              color: CONTRAST_TEXT,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: brand.primaryColorDark,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 16px ${alpha(brand.primaryColor, 0.3)}`,
              },
              '&:active': {
                transform: 'translateY(0)',
                boxShadow: `0 2px 8px ${alpha(brand.primaryColor, 0.2)}`,
                transition: 'all 0.1s ease-out',
              },
              '&:disabled': {
                backgroundColor: brand.textColorMuted,
                color: CONTRAST_TEXT,
                cursor: 'not-allowed',
              },
            },
          },
          {
            props: { variant: 'pill-outlined' as const },
            style: {
              width: '100%',
              borderRadius: 24,
              padding: '8px 16px',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: 'transparent',
              border: `2px solid ${brand.secondaryButtonColor}`,
              color: brand.secondaryButtonColor,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: brand.secondaryButtonColor,
                color: CONTRAST_TEXT,
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 16px ${alpha(brand.secondaryButtonColor, 0.3)}`,
              },
              '&:active': {
                transform: 'translateY(0)',
                boxShadow: `0 2px 8px ${alpha(brand.secondaryButtonColor, 0.2)}`,
                transition: 'all 0.1s ease-out',
              },
              '&:disabled': {
                backgroundColor: 'transparent',
                borderColor: brand.textColorMuted,
                color: brand.textColorMuted,
                cursor: 'not-allowed',
              },
            },
          },
        ],
      },

      MuiIconButton: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: brand.primaryColor,
            color: CONTRAST_TEXT,
            '&:hover': {
              backgroundColor: brand.primaryColorDark,
            },
          },
        },
        variants: [
          {
            props: { variant: 'recording' as const },
            style: {
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: brand.primaryColor,
              color: CONTRAST_TEXT,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                backgroundColor: brand.primaryColorDark,
                transform: 'scale(1.05)',
                boxShadow: `0 4px 12px ${alpha(brand.primaryColor, 0.4)}`,
              },
              '&:disabled': {
                backgroundColor: brand.primaryColorLight,
                color: CONTRAST_TEXT,
              },
            },
          },
          {
            props: {
              variant: 'recording' as const,
              color: 'secondary' as const,
            },
            style: {
              backgroundColor: 'transparent',
              border: `2px solid ${brand.secondaryButtonColor}`,
              color: brand.secondaryButtonColor,
              '&:hover': {
                backgroundColor: brand.secondaryButtonColor,
                color: CONTRAST_TEXT,
              },
            },
          },
          {
            props: { variant: 'recording' as const, color: 'error' as const },
            style: {
              backgroundColor: '#BF3131',
              color: CONTRAST_TEXT,
              '&:hover': {
                backgroundColor: '#991B1B',
                transform: 'scale(1.05)',
                boxShadow: '0 4px 12px rgba(191, 49, 49, 0.4)',
              },
            },
          },
          {
            props: { variant: 'recording' as const, color: 'warning' as const },
            style: {
              backgroundColor: 'transparent',
              border: `2px solid ${brand.errorColor}`,
              color: brand.errorColor,
              '&:hover': {
                backgroundColor: brand.errorColor,
                color: CONTRAST_TEXT,
              },
            },
          },
        ],
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: brand.backgroundColor,
          },
        },
        defaultProps: {
          disableScrollLock: true,
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: brand.menuHoverColor,
            },
            '&.Mui-selected': {
              backgroundColor: brand.menuActiveColor,
              '&:hover': {
                backgroundColor: brand.menuActiveColor,
              },
            },
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            minHeight: 40,
            fontSize: '14px',
            minWidth: 80,
            maxWidth: 280,
            fontWeight: 500,
            color: brand.textColorSecondary,
            '&:hover': {
              backgroundColor: brand.primaryColorHover,
            },
            '&.Mui-selected': {
              color: brand.activeTabColor,
              fontWeight: 600,
            },
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
          },
          indicator: {
            height: 3,
            backgroundColor: brand.activeTabColor,
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: {
            '& .MuiAlert-message': {
              fontSize: '14px',
            },
            '& .MuiAlert-icon': {
              fontSize: 20,
            },
          },
          standardWarning: {
            backgroundColor: '#fff8e1',
            borderColor: '#ff9800',
            color: '#e65100',
            '& .MuiAlert-icon': {
              color: '#ff9800',
            },
          },
          outlinedWarning: {
            borderColor: brand.warningColor,
            backgroundColor: '#fff8e1',
            '& .MuiAlert-message': {
              color: '#e65100',
            },
            '& .MuiAlert-icon': {
              color: '#ff9800',
            },
            '& .MuiAlert-action .MuiIconButton-root': {
              color: '#e65100',
            },
          },
          outlinedError: {
            borderColor: brand.errorColor,
          },
          outlinedSuccess: {
            borderColor: brand.successColor,
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: brand.backgroundColor,
            border: `1px solid ${brand.dividerColor}`,
            '&:hover': {
              backgroundColor: brand.backgroundColorAlt,
            },
          },
          colorPrimary: {
            backgroundColor: brand.primaryColorLight,
            color: brand.primaryColorDark,
            border: 'none',
          },
        },
        variants: [
          {
            props: { variant: 'transcript' as const },
            style: {
              backgroundColor: brand.backgroundColor,
              color: brand.textColor,
              borderRadius: '16px',
              maxWidth: '100%',
              width: 'fit-content',
              height: 'auto',
              minHeight: '32px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: `1px solid ${brand.dividerColor}`,
              '& .MuiChip-label': {
                display: 'block',
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto',
                lineHeight: 1.4,
                padding: '6px 12px',
                textAlign: 'left',
              },
              '&:hover': {
                backgroundColor: brand.backgroundHover,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              },
              transition: 'all 0.2s ease-in-out',
            },
          },
        ],
      },

      MuiTypography: {
        defaultProps: {
          variantMapping: {
            formLabel: 'p',
            sectionTitle: 'h3',
            menuItemPrimary: 'p',
            menuItemSecondary: 'p',
            placeholder: 'p',
            dialogTitle: 'h2',
            dialogBody: 'p',
            helperText: 'p',
            errorText: 'p',
            successText: 'p',
            statusText: 'span',
            statusReady: 'span',
            statusRecording: 'span',
            statusTranscribing: 'span',
            statusPaused: 'span',
            statusStarting: 'span',
            statusCompleted: 'span',
            statusWarning: 'span',
          },
        },
        variants: [
          {
            props: { variant: 'formLabel' as const },
            style: {
              fontWeight: 500,
              color: brand.textColorSecondary,
              fontSize: '0.875rem',
              marginBottom: '8px',
              display: 'block',
            },
          },
          {
            props: { variant: 'sectionTitle' as const },
            style: {
              fontWeight: 600,
              marginBottom: '8px',
            },
          },
          {
            props: { variant: 'menuItemPrimary' as const },
            style: {
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: 1.3,
              color: brand.textColor,
            },
          },
          {
            props: { variant: 'menuItemSecondary' as const },
            style: {
              fontSize: '12px',
              color: brand.textColorSecondary,
              lineHeight: 1.2,
            },
          },
          {
            props: { variant: 'placeholder' as const },
            style: {
              color: brand.textColorSecondary,
              fontSize: '14px',
            },
          },
          {
            props: { variant: 'dialogTitle' as const },
            style: {
              fontWeight: 600,
              fontSize: '1.1rem',
            },
          },
          {
            props: { variant: 'dialogBody' as const },
            style: {
              color: brand.textColorSecondary,
              lineHeight: 1.4,
            },
          },
          {
            props: { variant: 'body2' as const },
            style: {
              color: brand.textColorSecondary,
            },
          },
          {
            props: { variant: 'caption' as const },
            style: {
              color: brand.textColorSecondary,
            },
          },
          {
            props: { variant: 'helperText' as const },
            style: {
              fontSize: '12px',
              color: brand.textColorSecondary,
            },
          },
          {
            props: { variant: 'errorText' as const },
            style: {
              color: brand.errorColor,
            },
          },
          {
            props: { variant: 'successText' as const },
            style: {
              color: brand.successColor,
            },
          },
          {
            props: { variant: 'statusText' as const },
            style: {
              fontWeight: 500,
              fontSize: '12px',
            },
          },
          {
            props: { variant: 'statusReady' as const },
            style: {
              fontWeight: 500,
              fontSize: '11px',
              color: STATUS_COLORS.ready.text,
            },
          },
          {
            props: { variant: 'statusRecording' as const },
            style: {
              fontWeight: 500,
              fontSize: '11px',
              color: STATUS_COLORS.recording.text,
            },
          },
          {
            props: { variant: 'statusTranscribing' as const },
            style: {
              fontWeight: 500,
              fontSize: '11px',
              color: STATUS_COLORS.transcribing.text,
            },
          },
          {
            props: { variant: 'statusPaused' as const },
            style: {
              fontWeight: 500,
              fontSize: '11px',
              color: STATUS_COLORS.paused.text,
            },
          },
          {
            props: { variant: 'statusStarting' as const },
            style: {
              fontWeight: 500,
              fontSize: '11px',
              color: STATUS_COLORS.starting.text,
            },
          },
          {
            props: { variant: 'statusCompleted' as const },
            style: {
              fontWeight: 500,
              fontSize: '11px',
              color: STATUS_COLORS.completed.text,
            },
          },
          {
            props: { variant: 'statusWarning' as const },
            style: {
              fontWeight: 500,
              fontSize: '11px',
              color: STATUS_COLORS.warning.text,
            },
          },
        ],
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            '&.MuiTextField-colorWarning': {
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: brand.warningColor,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: brand.warningColor,
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: brand.warningColor,
              },
            },
            '&.MuiTextField-colorError': {
              '& .MuiOutlinedInput-root': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: brand.errorColor,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: brand.errorColor,
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: brand.errorColor,
              },
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: brand.backgroundColor,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: brand.primaryColor,
              borderWidth: 2,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: brand.primaryColor,
            },
          },
          notchedOutline: {
            borderColor: brand.dividerColor,
          },
        },
      },

      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: brand.textColorSecondary,
            '&.Mui-checked': {
              color: brand.primaryColor,
            },
          },
        },
      },

      MuiRadio: {
        styleOverrides: {
          root: {
            color: brand.textColorSecondary,
            '&.Mui-checked': {
              color: brand.primaryColor,
            },
          },
        },
      },

      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            marginRight: 0,
          },
          label: {
            fontSize: '14px',
            color: brand.textColor,
          },
        },
      },

      MuiCircularProgress: {
        styleOverrides: {
          colorPrimary: {
            color: brand.primaryColor,
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: brand.backgroundColor,
          },
        },
        variants: [
          {
            props: { variant: 'card' as const },
            style: {
              backgroundColor: brand.backgroundColor,
              borderRadius: parseInt(brand.borderRadius, 10) * 2 || 16,
              border: `1px solid ${brand.dividerColor}`,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              overflow: 'visible',
            },
          },
          {
            props: { variant: 'card-warning' as const },
            style: {
              backgroundColor: brand.backgroundColor,
              borderRadius: parseInt(brand.borderRadius, 10) * 2 || 16,
              border: `1px solid ${alpha(brand.warningColor, 0.5)}`,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              overflow: 'visible',
            },
          },
          {
            props: { variant: 'card-error' as const },
            style: {
              backgroundColor: brand.backgroundColor,
              borderRadius: parseInt(brand.borderRadius, 10) * 2 || 16,
              border: `1px solid ${alpha(brand.errorColor, 0.5)}`,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              overflow: 'visible',
            },
          },
          {
            props: { variant: 'panel' as const },
            style: {
              backgroundColor: brand.backgroundColorAlt,
              borderRadius: '6px',
              boxShadow: 'none',
              border: 'none',
            },
          },
          {
            props: { variant: 'dropdown' as const },
            style: {
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: `1px solid ${brand.dividerColor}`,
              borderRadius: '8px',
            },
          },
        ],
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: brand.dividerColor,
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: brand.backgroundColor,
          },
        },
      },
    },
  });
}
